import type { App, Plugin } from 'vue';
import {rafCallback} from "../util/raf";

export interface RefreshSPABuildOptions {
  /**
   * Interval in milliseconds to check for build changes.
   * @default 60000 (60 seconds)
   */
  interval?: number;

  /**
   * Meta tag name to check for build version.
   * @default 'app-build'
   */
  metaTagName?: string;
}


/**
 * Vue plugin that keeps a Single Page Application in sync with newly deployed builds
 * and prevents runtime failures caused by stale cached assets.
 *
 * ---------------------------------------------------------------------------
 * Motivation
 * ---------------------------------------------------------------------------
 * Modern SPAs typically use hashed filenames for JS/CSS bundles. When a new
 * version is deployed, those filenames change and old files are often removed.
 *
 * If a user keeps the app open:
 * - Their loaded index.html still references OLD asset hashes
 * - Lazy-loaded chunks may later return 404
 * - The SPA can enter a broken state (navigation fails, blank pages, errors)
 *
 * This plugin prevents that by:
 * - Periodically checking whether a newer build exists
 * - Listening for global chunk-load failures
 * - Triggering a full reload when a version mismatch is detected
 *
 * ---------------------------------------------------------------------------
 * How does it work?
 * ---------------------------------------------------------------------------
 * 1. Periodically fetches a fresh index.html
 * 2. Compares the fetched build with the current build
 * 3. Reloads the page when they differ
 * 4. Additionally reacts immediately to dynamic import / chunk load errors
 *
 * ---------------------------------------------------------------------------
 * Prerequisites
 * ---------------------------------------------------------------------------
 * 1. The index.html MUST contain a build identifier meta tag, e.g.:
 *    <meta name="app-build" content="BUILD_HASH">
 * 2. The value must change on every deployment
 * 3. index.html should NOT be aggressively cached by the server
 *   (recommended: `Cache-Control: no-cache`)
 * 4. Intended for SPAs using hashed asset filenames (Vite, Webpack, Rollup, etc.)
 */
export const RefreshSPABuild: Plugin<RefreshSPABuildOptions[]> = {
  // @ts-expect-error - unused argument
  install(app: App, options: RefreshSPABuildOptions = {}) {
    if (import.meta.env.MODE === 'development') {
      return;
    }

    const {
      interval = 60000,
      metaTagName = 'app-build',
    } = options;

    let currentBuild: string | null = null;
    let intervalId: number | null = null;

    /**
     * Extracts the build version from a given document.
     * @param doc - The Document to read the metadata from
     * @returns The extracted build version or null if the document did not set the build version.
     */
    function readBuildVersionFromDocument(doc: Document): string | null {
      const metaTag = doc.querySelector(`meta[name="${metaTagName}"]`);
      return metaTag?.getAttribute('content') || null;
    }

    async function checkBuildVersion() {
      try {
        // Fetch the index.html with cache-busting
        const response = await fetch(`/?t=${Date.now()}`, {
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
        });
        if (!response.ok) {
          return;
        }
        
        // parse the response as HTML document
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const newBuild = readBuildVersionFromDocument(doc);

        if (newBuild !== null && newBuild !== currentBuild) {
          if (intervalId !== null) {
            clearInterval(intervalId);
          }
          window.location.reload();
        }
      } catch (error) {
        console.error('[refreshSPAbuild] Error checking build version:', error);
      }
    }

    currentBuild = readBuildVersionFromDocument(document);
    intervalId = window.setInterval(rafCallback(checkBuildVersion), interval);

    // Ensures that chunk load errors are handled even if the interval did not yet
    // notice that the version changed.
    window.addEventListener("unhandledrejection", async (event) => {
      const msg = event.reason?.message || "";

      if (
        msg.includes("Failed to load module script") ||
        msg.includes("Failed to fetch dynamically imported module") ||
        msg.includes("Importing a module script failed")
      ) {
        await checkBuildVersion();
      }
    });
  },
};


